import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Flag as FlagIcon } from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { LEARNING_PATHS } from '../../constants/learningPaths';
import { recordAnalyticsEvent } from '../../api/analyticsApi';

const EXTRA_INTERESTS = [
  { id: 'career-support', fullLabel: 'Career support / mentoring' },
  { id: 'portfolio', fullLabel: 'Portfolio or GitHub support' },
  { id: 'not-sure', fullLabel: 'I am not sure yet' },
  { id: 'other', fullLabel: 'Something else' },
];

const OnboardingIntentModal = () => {
  const { user, submitIntent } = useAuth();
  const theme = useTheme();
  const [interests, setInterests] = useState([]);
  const [goal, setGoal] = useState('');
  const [preferredContact, setPreferredContact] = useState('email');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const shouldShow = useMemo(() => {
    if (!user || user.role !== 'learner' || !user.email_verified) return false;
    const hasPath = Array.isArray(user.learning_paths) && user.learning_paths.length > 0;
    const hasCohort = Boolean((user.cohort_key || '').trim());
    const hasIntent = Boolean(user.onboarding_intent?.submitted_at);
    return !hasPath && !hasCohort && !hasIntent;
  }, [user]);

  useEffect(() => {
    if (shouldShow) {
      recordAnalyticsEvent('onboarding_modal_viewed', { user_id: user.id });
    }
  }, [shouldShow, user?.id]);

  if (!shouldShow) return null;

  const options = [...LEARNING_PATHS, ...EXTRA_INTERESTS];

  const toggleInterest = (id) => {
    setInterests((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      await submitIntent({
        interests,
        goal,
        preferred_contact: preferredContact,
      });
    } catch (submitError) {
      setError(submitError.message || 'Could not send your request.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" PaperProps={{ elevation: 8, sx: { borderRadius: 2 } }}>
      <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <FlagIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>
                What are you here to work on?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This helps the Qode Clarity team assign the right path or follow up by email.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>
              Main interest
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
              {options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={interests.includes(option.id)}
                      onChange={() => toggleInterest(option.id)}
                    />
                  }
                  label={option.fullLabel}
                  sx={{ mr: 1, minWidth: { xs: '100%', sm: '45%' } }}
                />
              ))}
            </Stack>
          </Box>

          <TextField
            label="Tell us what you are hoping to achieve"
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
            multiline
            minRows={4}
            placeholder="Example: I want to move into data analysis, improve Excel/SQL, and build portfolio evidence for junior analyst roles."
            fullWidth
          />

          <TextField
            label="Best way to follow up"
            value={preferredContact}
            onChange={(event) => setPreferredContact(event.target.value)}
            placeholder="Email, WhatsApp, phone, or another preference"
            fullWidth
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || (!interests.length && !goal.trim())}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {saving ? 'Sending…' : 'Send to admin'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {!saving && !interests.length && !goal.trim()
                ? 'Pick at least one interest above, or write your goal, to enable this button.'
                : 'You can still browse after sending this. An admin will review it.'}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingIntentModal;
